/**
 * Normalize a raw phone number (from Apple Pay contact) into E.164 format.
 *
 * This function is Saudi-first: it knows about the +966 country code and the
 * local 05xxxxxxxx format. For non-Saudi numbers, it relies on Apple Pay
 * providing a full international number (which it does for most locales).
 *
 * Returns null when the number is too short to be valid (< 9 significant digits).
 *
 * Supported input forms:
 *   "0512345678"   -> "+966512345678"   (Saudi local with leading 0)
 *   "0512345678"   -> "+966512345678"
 *   "00966512..."  -> "+966512..."      (international prefix 00)
 *   "+966512..."   -> "+966512..."      (already E.164)
 *   "512345678"    -> "+966512345678"   (9-digit Saudi without prefix)
 *   "+4412345..."  -> "+4412345..."     (non-Saudi, passed through)
 */
export function normalizePhoneNumber(raw: string): string | null {
  let digitsOnly = raw.replace(/\D/g, '');

  // Strip international dialing prefix (00) — equivalent to the + sign
  if (digitsOnly.startsWith('00')) {
    digitsOnly = digitsOnly.substring(2);
  }

  // Saudi local format: 05xxxxxxxx (10 digits with leading 0) -> strip the 0
  // to get the 9-digit subscriber number 5xxxxxxxx
  if (digitsOnly.startsWith('05') && digitsOnly.length === 10) {
    digitsOnly = digitsOnly.substring(1);
  }

  // Reject obviously invalid numbers (fewer than 9 significant digits)
  if (digitsOnly.length < 9) {
    return null;
  }

  if (digitsOnly.startsWith('966') && digitsOnly.length === 12) {
    // Full Saudi number with country code: 966512345678
    return `+${digitsOnly}`;
  } else if (digitsOnly.startsWith('5') && digitsOnly.length === 9) {
    // Saudi subscriber number without country code: 512345678
    return `+966${digitsOnly}`;
  } else if (digitsOnly.length >= 10) {
    // International number that includes a country code (non-Saudi or already
    // prefixed). Apple Pay typically provides full international numbers for
    // non-Saudi locales, so prepend + to restore E.164 format.
    return `+${digitsOnly}`;
  } else {
    // 9 digits not starting with 5 — uncommon edge case.
    // Assume Saudi (e.g. landline starting with a different digit) and prepend
    // country code. This may produce an invalid number for non-Saudi inputs,
    // but the downstream guest-creation API will validate the number.
    return `+966${digitsOnly}`;
  }
}
